// File handling utilities for image and PDF processing

export const processFileUpload = async (file, isGraphingMode) => {
    // Check file size for PDFs (2MB limit)
    if (file.type === 'application/pdf') {
        const TWO_MB = 2 * 1024 * 1024; // 2MB in bytes
        if (file.size > TWO_MB) {
            throw new Error('PDF file size must be less than 2MB');
        }
    }

    if (isGraphingMode) {
        // Handle CSV file for graphing mode
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const csvData = event?.target?.result;
                resolve({
                    file_name: file.name,
                    content: csvData,
                    type: 'csv'
                });
            };
            reader.onerror = () => reject(new Error('Failed to read CSV file'));
            reader.readAsText(file);
        });
    } else if (file.type === 'application/pdf') {
        // Handle PDF file
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const arrayBuffer = event.target.result;

                try {
                    // Parse PDF using PDF.js
                    const pdf = await window.pdfjsLib.getDocument(arrayBuffer).promise;

                    // Check for page count limit
                    if (pdf.numPages > 10) {
                        reject(new Error('PDF cannot have more than 10 pages.'));
                        return;
                    }

                    let text = '';
            
                    for (let i = 1; i <= pdf.numPages; i++) {
                        const page = await pdf.getPage(i);
                        const content = await page.getTextContent();
                        const pageText = content.items.map(item => item.str).join(' ');
                        text += pageText + '\n\n';
                    }

                    // Log the parsed text to console
            
                    
                    resolve({
                        file_name: file.name,
                        content: text,
                        url: URL.createObjectURL(file),
                        type: 'pdf'
                    });
                } catch (error) {
                    console.error('Error parsing PDF:', error);
                    reject(new Error('Failed to parse PDF file'));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read PDF file'));
            reader.readAsArrayBuffer(file);
        });
    } else {
        // Handle image selection for regular mode (upload handled by backend API)
        return {
            file_name: file.name,
            url: URL.createObjectURL(file),
            file,
            type: 'image'
        };
    }
};

// Initialize PDF.js scripts
export const initializePDFJS = () => {
    return new Promise((resolve, reject) => {
        // Load PDF.js scripts
        const pdfScript = document.createElement('script');
        pdfScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.min.js';
        pdfScript.async = true;
        document.head.appendChild(pdfScript);

        pdfScript.onload = () => {
            const workerScript = document.createElement('script');
            workerScript.textContent = `
                window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.worker.min.js';
            `;
            document.head.appendChild(workerScript);
            resolve();
        };

        pdfScript.onerror = () => {
            reject(new Error('Failed to load PDF.js'));
        };
    });
};

// File validation utilities
export const validateFile = (file, isGraphingMode) => {
    if (!file) return { isValid: false, error: 'No file selected' };

    if (isGraphingMode) {
        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            return { isValid: false, error: 'Please select a CSV file for graphing mode' };
        }
    } else {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            return { isValid: false, error: 'Please select an image file (JPEG, PNG, GIF) or PDF file' };
        }
    }

    return { isValid: true };
};

// File size formatter
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}; 